/**
 * SchedulingListView - Visualização em lista dos agendamentos
 * Alternativa ao calendário, exibe atividades agrupadas por data
 * Suporta scroll infinito com IntersectionObserver
 * 
 * @version 2.0.0
 * RELEASE: 2026-02-14 v1.0.0 - Implementação inicial da visualização em lista
 * RELEASE: 2026-02-14 v2.0.0 - Scroll infinito com IntersectionObserver + sentinela
 */
import React, { useRef, useEffect, useCallback } from 'react';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarX, CheckCircle2, Clock, ExternalLink, Link2, Loader2, XCircle } from 'lucide-react';
import googleMeetIcon from '@/assets/google-meet-icon.png';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ACTIVITY_COLORS, ACTIVITY_LABELS, ACTIVITY_ICONS } from '@/hooks/useCalendarActivities';
import { cn } from '@/lib/utils';

/** Tipo de atividade vindo do hook (com flag standalone opcional) */
interface ActivityItem {
  id: string;
  title: string;
  description: string | null;
  type: 'note' | 'call' | 'meeting' | 'task' | 'email';
  scheduled_at: string;
  completed_at: string | null;
  performed_by_user_id: string | null;
  deal_id: string | null;
  deal_title?: string;
  customer_name?: string;
  assigned_user_name?: string;
  conferencing_link?: string | null;
  /** Flag indicando que é um booking standalone (sem deal) */
  is_standalone_booking?: boolean;
  /**
   * (Opcional) Ícone customizado por item — sobrepõe o ícone padrão
   * derivado de `type`. Permite ao consumidor (ex.: Calendário Global)
   * representar fontes diferentes na mesma lista.
   */
  iconNode?: React.ReactNode;
  /**
   * (Opcional) Cor do indicador (bolinha) — sobrepõe a cor padrão
   * derivada de `type`. Aceita qualquer string CSS válida.
   */
  dotColor?: string;
  /** (Opcional) Tooltip do ícone customizado */
  iconTitle?: string;
}

/** Grupo de atividades por data */
interface DateGroup {
  dateLabel: string;
  dateKey: string;
  items: ActivityItem[];
}

interface SchedulingListViewProps {
  /** Atividades brutas do hook */
  activities: ActivityItem[];
  /** Estado de carregamento inicial */
  isLoading: boolean;
  /** Callback ao clicar em um negócio */
  onOpenDeal?: (dealId: string) => void;
  /** Callback ao clicar em uma atividade */
  onOpenActivity?: (activity: ActivityItem) => void;
  /** Indica se há mais páginas para carregar */
  hasNextPage?: boolean;
  /** Função para carregar próxima página */
  fetchNextPage?: () => void;
  /** Indica se está carregando próxima página */
  isFetchingNextPage?: boolean;
  /** Callback para vincular booking standalone a um deal */
  onLinkToDeal?: (bookingId: string, guestName: string) => void;
}

/**
 * Formata label amigável para a data
 */
function formatGroupLabel(date: Date): string {
  if (isToday(date)) return 'Hoje';
  if (isTomorrow(date)) return 'Amanhã';
  return format(date, "EEEE, d 'de' MMMM", { locale: ptBR });
}

/**
 * Agrupa atividades por data de agendamento
 */
function groupActivitiesByDate(activities: ActivityItem[]): DateGroup[] {
  const groupsMap = new Map<string, { date: Date; items: ActivityItem[] }>();

  // Ordena por data agendada (mais próximas primeiro)
  const sorted = [...activities].sort(
    (a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()
  );

  for (const item of sorted) {
    const date = new Date(item.scheduled_at);
    const dateKey = format(date, 'yyyy-MM-dd');

    if (!groupsMap.has(dateKey)) {
      groupsMap.set(dateKey, { date, items: [] });
    }
    groupsMap.get(dateKey)!.items.push(item);
  }

  return Array.from(groupsMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, { date, items }]) => ({
      dateKey,
      dateLabel: formatGroupLabel(date),
      items,
    }));
}

/**
 * Componente de visualização em lista dos agendamentos
 * v2.0.0: Suporta scroll infinito via IntersectionObserver
 */
export function SchedulingListView({
  activities,
  isLoading,
  onOpenDeal,
  onOpenActivity,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  onLinkToDeal,
}: SchedulingListViewProps) {
  // Referências para IntersectionObserver
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  /**
   * IntersectionObserver para detectar quando o sentinela fica visível
   * e disparar o carregamento da próxima página
   */
  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage && fetchNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(handleIntersect, {
      root: container,
      rootMargin: '200px',
      threshold: 0,
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleIntersect]);

  // Estado de carregamento inicial
  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border p-4 space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-5 w-20" />
          </div>
        ))}
      </div>
    );
  }

  // Estado vazio
  if (activities.length === 0) {
    return (
      <div className="bg-card rounded-lg border p-12 flex flex-col items-center justify-center text-center">
        <CalendarX className="h-12 w-12 text-muted-foreground/40 mb-3" />
        <p className="text-sm font-medium text-muted-foreground">
          Nenhum agendamento encontrado
        </p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Ajuste os filtros ou crie um novo agendamento
        </p>
      </div>
    );
  }

  // Agrupa atividades por data
  const groups = groupActivitiesByDate(activities);

  return (
    <div
      ref={scrollContainerRef}
      className="bg-card rounded-lg border overflow-y-auto scrollbar-thin flex-1"
      style={{ maxHeight: 'calc(100vh + 40px)' }}
    >
      <div className="divide-y divide-border">
        {groups.map((group) => (
          <div key={group.dateKey}>
            {/* Separador de data */}
            <div className="px-4 py-2 bg-muted/50 border-b border-border sticky top-0 z-10">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {group.dateLabel}
              </span>
              <span className="text-xs text-muted-foreground/60 ml-2">
                ({group.items.length})
              </span>
            </div>

            {/* Itens do grupo */}
            <div className="divide-y divide-border/50">
              {group.items.map((item) => {
                const isCompleted = !!item.completed_at;
                const scheduledDate = new Date(item.scheduled_at);
                const isOverdue = !isCompleted && isPast(scheduledDate);
                const colors = ACTIVITY_COLORS[item.type] || ACTIVITY_COLORS.note;

                return (
                  <div
                    key={item.id}
                    className={cn(
                      'flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-2 sm:px-4 py-2.5 hover:bg-muted/30 transition-colors cursor-pointer group',
                      isCompleted && 'opacity-60'
                    )}
                    onClick={() => onOpenActivity?.(item)}
                  >
                    {/* Linha 1 mobile / Linha única desktop: indicador + horário + ícone + título */}
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 sm:flex-1">
                      {/* Indicador de cor por tipo (ou cor customizada via item.dotColor) */}
                      <div
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: item.dotColor ?? colors.bg }}
                        title={item.iconTitle ?? ACTIVITY_LABELS[item.type]}
                      />

                      {/* Horário */}
                      <span
                        className={cn(
                          'text-xs font-mono w-12 shrink-0',
                          isCompleted
                            ? 'text-muted-foreground line-through'
                            : isOverdue
                              ? 'text-destructive font-medium'
                              : 'text-muted-foreground'
                        )}
                      >
                        {format(scheduledDate, 'HH:mm')}
                      </span>

                      {/* Ícone do tipo (ou ícone customizado via item.iconNode) */}
                      <span className="text-sm shrink-0 flex items-center" title={
                        item.iconTitle ??
                        (item.type === 'meeting' && item.conferencing_link
                          ? 'Agendamento Online'
                          : ACTIVITY_LABELS[item.type])
                      }>
                      {item.iconNode
                          ? item.iconNode
                          : item.type === 'meeting' && (item.conferencing_link || (item as any).is_standalone_booking)
                            ? <img src={googleMeetIcon} alt="Google Meet" className="h-4 w-4" />
                            : ACTIVITY_ICONS[item.type]}
                      </span>

                      {/* Título */}
                      <span
                        className={cn(
                          'text-sm truncate flex-1 min-w-0',
                          isCompleted
                            ? 'line-through text-muted-foreground'
                            : 'text-foreground'
                        )}
                      >
                        {item.title}
                      </span>

                      {/* Badge de status - visível apenas no desktop inline */}
                      <div className="hidden sm:flex items-center gap-2 shrink-0">
                        {/* Negócio (se vinculado) */}
                        {item.deal_title ? (
                          <button
                            className="text-xs text-primary/70 hover:text-primary truncate max-w-[150px] hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.deal_id) onOpenDeal?.(item.deal_id);
                            }}
                            title={item.deal_title}
                          >
                            {item.deal_title}
                          </button>
                        ) : (item as any).is_standalone_booking ? (
                          <button
                            className="flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-primary transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              onLinkToDeal?.(item.id, item.customer_name || item.title);
                            }}
                            title="Vincular a um negócio"
                          >
                            <Link2 className="h-3 w-3" />
                            <span>Sem negócio</span>
                          </button>
                        ) : null}

                        {/* Link de conferência */}
                        {item.conferencing_link && (
                          <a
                            href={item.conferencing_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary/60 hover:text-primary"
                            onClick={(e) => e.stopPropagation()}
                            title="Abrir link da reunião"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}

                        {/* Badge de status */}
                        {isCompleted && item.description?.startsWith('❌ CANCELADO') ? (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-red-50 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400">
                            <XCircle className="h-3 w-3 mr-0.5" />
                            Cancelado
                          </Badge>
                        ) : isCompleted ? (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                            <CheckCircle2 className="h-3 w-3 mr-0.5" />
                            Concluído
                          </Badge>
                        ) : isOverdue ? (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-5">
                            <Clock className="h-3 w-3 mr-0.5" />
                            Atrasado
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                            <Clock className="h-3 w-3 mr-0.5" />
                            Pendente
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Linha 2 mobile: deal + link + badge */}
                    <div className="flex items-center gap-2 pl-7 sm:hidden">
                      {/* Negócio ou "Sem negócio" */}
                      {item.deal_title ? (
                        <button
                          className="text-[11px] text-primary/70 hover:text-primary truncate max-w-[160px] hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.deal_id) onOpenDeal?.(item.deal_id);
                          }}
                          title={item.deal_title}
                        >
                          {item.deal_title}
                        </button>
                      ) : (item as any).is_standalone_booking ? (
                        <button
                          className="flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-primary transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLinkToDeal?.(item.id, item.customer_name || item.title);
                          }}
                          title="Vincular a um negócio"
                        >
                          <Link2 className="h-3 w-3" />
                          <span>Sem negócio</span>
                        </button>
                      ) : null}

                      {/* Link de conferência (mobile) */}
                      {item.conferencing_link && (
                        <a
                          href={item.conferencing_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary/60 hover:text-primary"
                          onClick={(e) => e.stopPropagation()}
                          title="Abrir link da reunião"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}

                      {/* Badge de status (mobile) - alinhado à direita */}
                      <div className="ml-auto shrink-0">
                        {isCompleted && item.description?.startsWith('❌ CANCELADO') ? (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-red-50 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400">
                            <XCircle className="h-3 w-3 mr-0.5" />
                            Cancelado
                          </Badge>
                        ) : isCompleted ? (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                            <CheckCircle2 className="h-3 w-3 mr-0.5" />
                            Concluído
                          </Badge>
                        ) : isOverdue ? (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-5">
                            <Clock className="h-3 w-3 mr-0.5" />
                            Atrasado
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                            <Clock className="h-3 w-3 mr-0.5" />
                            Pendente
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Sentinela para IntersectionObserver - dispara carregamento da próxima página */}
        <div ref={sentinelRef} className="h-px" />

        {/* Indicador de carregamento da próxima página */}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Carregando mais agendamentos...</span>
          </div>
        )}
      </div>
    </div>
  );
}
